import React from "react";
import FeatherIcon from "feather-icons-react";
import {
  isDocumentAccountingClosed,
  isDocumentFullyPaid,
} from "../../../utils/documentMatchAccounting";

/**
 * Book icon + pie-chart badge for document accounting:
 * CSS conic: 0–180° = right (match), 180–360° = left (emission)
 * - full gray: missing emission and match
 * - left amber / right gray: emission only, or match not fully paid
 * - left gray / right amber: match paid+posted, no emission
 * - full green + check: closed (paid + emission + all match asientos)
 */
function statusFromEntries(entriesData) {
  const completed = entriesData?.completed === true;
  const amount = Number(entriesData?.amount || 0);
  const inProgress = !completed && amount > 0;
  return { completed, inProgress };
}

function statusLabel(status) {
  if (!status) return "Pendiente";
  if (status.completed) return "Contabilizado";
  if (status.inProgress) return "En progreso";
  return "Pendiente";
}

const ORANGE = "#F5A623";
const GRAY = "#9CA3AF";
const BOOK_GRAY = "#6B7280";
// Same as the original completed asiento badge (green-100 + green-700 check).
const CONCILIADO_BG = "#DCFCE7";
const CONCILIADO_ICON = "#15803D";
const CONCILIADO_OUTLINE = "#064E3B";

function pieBackground({ hasEmission, hasMatch, isDocumentBadge, overallCompleted, overallInProgress }) {
  if (!isDocumentBadge) {
    if (overallCompleted) return CONCILIADO_BG;
    if (overallInProgress) return "#F97316";
    return GRAY;
  }
  if (overallCompleted) return CONCILIADO_BG;
  if (hasEmission && !hasMatch) {
    // Right = match (gray), left = emission (amber)
    return `conic-gradient(${GRAY} 0deg 180deg, ${ORANGE} 180deg 360deg)`;
  }
  if (!hasEmission && hasMatch) {
    // Right = match (amber), left = emission (gray)
    return `conic-gradient(${ORANGE} 0deg 180deg, ${GRAY} 180deg 360deg)`;
  }
  if (hasEmission && hasMatch) return ORANGE;
  return GRAY;
}

export default function AccountingBadge({
  entryStatus,
  entries,
  matchAccounting,
  documentType,
  remaining,
  onClick,
  size = "default",
}) {
  const entriesData = entries || entryStatus;
  const emissionStatus = statusFromEntries(entriesData);
  const isDocumentBadge = matchAccounting != null;
  const hasEmission = emissionStatus.completed === true;
  const fullyPaid = isDocumentFullyPaid(remaining);
  const allMatchesPosted =
    matchAccounting?.applicable === true && matchAccounting?.completed === true;
  // Right half lights only when the document is fully paid and has match asientos.
  const hasMatchLayer =
    fullyPaid &&
    (Number(matchAccounting?.completedCount || 0) > 0 || matchAccounting?.completed === true);
  const showMatch = matchAccounting?.applicable === true;
  const matchStatus = showMatch
    ? {
        completed: allMatchesPosted,
        inProgress:
          !allMatchesPosted && Number(matchAccounting.completedCount || 0) > 0,
      }
    : null;

  const overallCompleted = isDocumentBadge
    ? isDocumentAccountingClosed(
        { documentType, entries: entriesData, remaining, meta: { remaining } },
        matchAccounting,
      )
    : emissionStatus.completed;
  const overallInProgress =
    !overallCompleted &&
    (emissionStatus.completed ||
      emissionStatus.inProgress ||
      (matchStatus && (matchStatus.completed || matchStatus.inProgress)));

  const sizeConfig = {
    small: {
      iconSize: "16px",
      badgeSize: "14px",
      badgeTop: "-4px",
      badgeRight: "-4px",
    },
    default: {
      iconSize: "18px",
      badgeSize: "16px",
      badgeTop: "-5px",
      badgeRight: "-5px",
    },
  };

  const config = sizeConfig[size] || sizeConfig.default;
  const strokeWidthToMatchComment = 4 / 3;
  const bookColor = BOOK_GRAY;
  const badgeBackground = pieBackground({
    hasEmission,
    hasMatch: hasMatchLayer,
    isDocumentBadge,
    overallCompleted,
    overallInProgress,
  });

  const matchTitle = showMatch
    ? `Match/pago: ${statusLabel(matchStatus)}${
        matchAccounting.pendingCount
          ? ` (${matchAccounting.pendingCount} de ${matchAccounting.totalCount} pendientes)`
          : ` (${matchAccounting.totalCount})`
      }`
    : "Match/pago: Pendiente";

  const title = isDocumentBadge
    ? overallCompleted
      ? "Contabilizado (emisión y match)"
      : `Emisión: ${hasEmission ? "Contabilizado" : "Pendiente"}\n${matchTitle}`
    : overallCompleted
      ? "Contabilizado"
      : overallInProgress
        ? "Contabilización en progreso"
        : "Pendiente de contabilizar";

  const showCheck = overallCompleted;

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: onClick ? "pointer" : "default",
        width: config.iconSize,
        height: config.iconSize,
      }}
      title={title}
    >
      <div style={{ width: config.iconSize, height: config.iconSize, display: "block" }}>
        <FeatherIcon
          icon="book"
          size={config.iconSize}
          strokeWidth={strokeWidthToMatchComment}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ display: "block", color: bookColor }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          top: config.badgeTop,
          right: config.badgeRight,
          width: config.badgeSize,
          height: config.badgeSize,
          borderRadius: "50%",
          background: badgeBackground,
          color: overallCompleted ? CONCILIADO_ICON : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #fff",
          boxSizing: "border-box",
        }}
      >
        {showCheck ? (
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FeatherIcon
              icon="check"
              size={Math.max(9, parseInt(config.badgeSize, 10) - 6)}
              color={CONCILIADO_OUTLINE}
              strokeWidth={strokeWidthToMatchComment * 1.9}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: "absolute", inset: 0, margin: "auto" }}
            />
            <FeatherIcon
              icon="check"
              size={Math.max(9, parseInt(config.badgeSize, 10) - 6)}
              color={CONCILIADO_ICON}
              strokeWidth={strokeWidthToMatchComment}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: "absolute", inset: 0, margin: "auto" }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
